/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import NavBar from "../../components/NavBar/NavBar";
import { getChargerToQr } from "../../stores/actions/qr_action";

import { button_text } from "../../styles/colors";
import KAKAO_LOGO from "../../assets/images/kakao_navi_icon.png";
import E_PIT_LOGO from "../../assets/images/e_pit_icon.png";
import SEOULEV_APP_ICON from "../../assets/images/seoulev_app_icon.png";
import CREDIT_CARD_ICON from "../../assets/images/credit_card_icon.svg";

const APP_SCHEME = "hyundaiepit://";
const ANDROID_PACKAGE = "com.hyundai.acteev";

const APP_STORE_URL = "https://apps.apple.com/app/id1556412770";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=" + ANDROID_PACKAGE;

// 서울이브이 앱 딥링크/스토어 (QR 첫 진입 시 "앱 열기" 안내용)
const SEOULEV_APP_SCHEME = "kr.seoulev.app";
const SEOULEV_APP_DEEPLINK_HOST = "qr";
const SEOULEV_ANDROID_PACKAGE = "kr.seoulev.SEOULEVapp";
const SEOULEV_PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=" + SEOULEV_ANDROID_PACKAGE;
const SEOULEV_APP_STORE_URL =
  "https://apps.apple.com/kr/app/seoulev-서울이브이/id6444150927";

const userAgent = navigator.userAgent.toLowerCase();
const isAndroid = userAgent.indexOf("android") > -1;
const isIOS = userAgent.indexOf("iphone") > -1 || userAgent.indexOf("ipad") > -1;

//QR 플랫폼 (private : 회원만 접근가능)
const qrPlatforms = [
  {
    name: "E-pit",
    src: E_PIT_LOGO,
    alt: "E-pit 결제",
    route:
      getEpitAppUrl()
    ,
    private: false,
    label: "E-pit",
  },
  {
    name: "kakao",
    src: KAKAO_LOGO,
    alt: "KakaoNavi QR결제",
    route: new URL(
      "/web2app/bridge",
      "https://carner.kakaomobility.com"
    ),
    private: false,
    label: "카카오내비",
  },
  {
    name: "seoulev",
    src: CREDIT_CARD_ICON,
    alt: "서울이브이 QR결제",
    route: "/QRpay",
    private: true,
    label: '비회원'
  },
];


function getEpitAppUrl() {
  // 현재 디바이스 체크
  if (isAndroid) {
    // 안드로이드인 경우
    return `intent://${APP_SCHEME}#Intent;package=${ANDROID_PACKAGE};scheme=https;end`;
  } else if (isIOS) {
    // iOS인 경우
    return APP_SCHEME;
  }
}

// 서울이브이 앱 열기: 설치돼 있으면 앱 실행(kr.seoulev.app://qr?code=...),
// 미설치면 스토어로 폴백한다.
function openSeoulevApp(qrcode) {
  const path = `${SEOULEV_APP_DEEPLINK_HOST}?code=${encodeURIComponent(qrcode)}`;
  if (isAndroid) {
    // intent:// 의 browser_fallback_url 로 미설치 시 Play 스토어 자동 이동
    const fallback = encodeURIComponent(SEOULEV_PLAY_STORE_URL);
    window.location.href = `intent://${path}#Intent;scheme=${SEOULEV_APP_SCHEME};package=${SEOULEV_ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
  } else if (isIOS) {
    // iOS 는 설치 여부 확인 API 가 없어 타이머로 폴백.
    // 앱이 열려 페이지가 백그라운드로 가면(visibilitychange) 타이머를 취소한다.
    window.location.href = `${SEOULEV_APP_SCHEME}://${path}`;
    const timer = setTimeout(() => {
      window.location.href = SEOULEV_APP_STORE_URL;
    }, 2000);
    document.addEventListener("visibilitychange", function onHide() {
      if (document.hidden) {
        clearTimeout(timer);
        document.removeEventListener("visibilitychange", onHide);
      }
    });
  }
}

function _linkTo(index, qrcode) {
  const obj = qrPlatforms[index];
  if (obj.name === "kakao") {
    // 디코딩된 딥링크 URL 구성 : https://carner.kakaomobility.com/web2app/bridge?action=kakaonavi%3A%2F%2Fcar_electro%3Fdata%3D%7B%22code%22%3A%22큐알코드%22%7D%26
    // 인코딩된 딥링크 URL 구성: kakaonavi://car_electro?data={"code":"큐알코드"}&
    const deepLinkUrl = `kakaonavi://car_electro?data=${JSON.stringify({ code: qrcode })}&`;
    // action 파라미터에 딥링크 URL을 인코딩하여 설정
    obj.route.searchParams.set("action", deepLinkUrl);
    return obj.route.href;
  }
  if (obj.name === "E-pit") {
    return obj.route;
  }
  return `${obj.route}/${qrcode}`;
}

function QRplatformPage() {
  const user = useState(useSelector((state) => state.user));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const param = useParams();
  let [isChargerReady, setIsChargerReady] = useState(false);
  let qrcode = param["qrcode"];
  useEffect(() => {
    if (qrcode) {
      dispatch(getChargerToQr(qrcode))
        .then((response) => {
          if (response.payload.result === "OK") {
            // OK : 조회 성공
            const ready = response.payload.data.sttus === "AB";
            setIsChargerReady(ready);
            // 충전기 사용 가능 + 모바일일 때만 서울이브이 앱 열기 안내.
            // 확인 → 앱 실행/스토어 폴백, 취소 → 페이지에 머무르며 아래 플랫폼 선택 진행.
            if (ready && (isAndroid || isIOS)) {
              if (window.confirm("'SEOULEV' 앱을 여시겠습니까?\n확인 버튼을 누르면\n서울이브이 앱으로 이동합니다.")) {
                openSeoulevApp(qrcode);
              }
            }
          } else {
            // FAIL : 실패
            setIsChargerReady(false);
            alert(response.payload.msg);
          }
        })
        .catch((e) => {
          setIsChargerReady(false);
          alert("비회원 현장결제를 지원하지 않는 충전기입니다.");
        });
    } else {
      navigate("*");
    }
  }, []);

  // 진입 시 자동 confirm 을 취소해도 다시 앱으로 이동할 수 있는 버튼 핸들러.
  // 모바일: 딥링크(설치 시 앱 실행 / 미설치 시 스토어 폴백), PC: 모바일 전용 안내.
  const handleOpenSeoulevApp = () => {
    if (!isChargerReady) return;
    if (isAndroid || isIOS) {
      // 진입 시 자동 confirm 과 동일한 문구로 재확인 후 이동
      if (
        window.confirm(
          "'SEOULEV' 앱을 여시겠습니까?\n확인 버튼을 누르면\n서울이브이 앱으로 이동합니다."
        )
      ) {
        openSeoulevApp(qrcode);
      }
    } else {
      alert(
        "모바일 기기에서만 앱으로 이동할 수 있습니다.\n휴대폰으로 QR을 다시 스캔해 접속해주세요."
      );
    }
  };

  return (
    <>
      <NavBar isNavBtnVisible={false} />
      <div css={QRpayWrap}>
        <Card>
          <Card.Body>
            <div
              css={css`
                padding: 5%;
              `}
            >
              <ol
                css={css`
                  line-height: 150%;
                  word-break: keep-all;
                `}
              >
                <li>* QR 결제방식 안내</li>
                <br />
                <li>
                  1. 이용하고자 하는 서비스를 선택하여, QR 결제를 진행합니다.
                </li>
                <li>
                  2. 보다 자세한 QR 결제 진행 방법은 각 서비스에 접속하여
                  확인해주시기 바랍니다.
                </li>
              </ol>

              <div
                css={css`
                  width: 100%;
                  display: flex;
                  justify-content: center;
                `}
              >
                <div
                  css={css`
                    padding-top: 5%;
                    width: 100%;
                    max-width: 500px;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                    justify-items: center;

                    /* 모바일 해상도: 2칸 2줄 */
                    @media (max-width: 480px) {
                      grid-template-columns: repeat(2, 1fr);
                      max-width: 260px;
                      row-gap: 20px;
                    }
                  `}
                >
                  <div
                    onClick={handleOpenSeoulevApp}
                    css={css`
                      display: flex;
                      flex-direction: column;
                      cursor: pointer;
                    `}
                  >
                    <div css={isChargerReady ? btnActive : btnInactive}>
                      <img
                        src={SEOULEV_APP_ICON}
                        alt="서울이브이 앱 아이콘"
                        css={css`
                          width: 100%;
                          object-fit: contain;
                          object-position: center;
                        `}
                      />
                    </div>
                    <div>
                      <p
                        css={css`
                          text-align: center;
                          padding-top: 5px;
                        `}
                      >
                        서울이브이
                      </p>
                    </div>
                  </div>
                  {Array.from({ length: 3 }).map((_, i) =>
                    qrPlatforms[i] != null ? (
                      <Link
                        to={isChargerReady ? _linkTo(i, qrcode) : null}
                        css={css`
                          display: flex;
                          flex-direction: column;
                        `}
                        onClick={() => {
                          if (qrPlatforms[i].name === "E-pit") {
                            if (isIOS) {
                              setTimeout(() => {
                                window.location.href = APP_STORE_URL;
                              }, 2000);
                            } else {
                              setTimeout(() => {
                                window.location.href = PLAY_STORE_URL;
                              }, 2000);
                            }
                          }
                        }}
                      >
                        <div
                          key={i}
                          css={isChargerReady ? btnActive : btnInactive}
                        >
                          <img
                            src={qrPlatforms[i].src}
                            alt="QR platform logo"
                            css={css`
                              width: 100%;
                              object-fit: contain;
                              object-position: center;
                            `}
                          />
                        </div>
                        <div>
                          {qrPlatforms[i]?.label && (
                            <p
                              css={css`
                                text-align: center;
                                padding-top: 5px;
                              `}
                            >
                              {qrPlatforms[i].label}
                            </p>
                          )}
                        </div>
                      </Link>
                    ) : (
                      <div key={i} css={btnInactive}>
                        <p
                          css={css`
                            word-break: keep-all;
                            text-align: center;
                          `}
                        >
                          서비스 준비중
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div >
    </>
  );

}

const QRpayWrap = css`
  padding: 5%;
  input[type="checkbox"] {
    display: none;
  }
  .card {
    border: none;
    h1 {
      padding-bottom: 32px;
      font-weight: 700;
      font-size: 24px;
      line-height: 24px;
    }
    .card-body {
      padding: 0;

      .form_row {
        display: flex;
        padding-bottom: 24px;

        .input_div {
          width: 100%;
          height: 48px;
        }

        .label_div {
          font-weight: 400;
          font-size: 16px;
          line-height: 225.3%;
          color: ${button_text};

          min-width: 115px;
        }

        .text_div {
          width: 100%;
        }

        textarea {
          border-radius: 10px;
        }

        input[type="text"] {
          width: 100%;
          height: 100%;
          border-radius: 10px;
        }
      }
    }
  }
`;

const btnActive = css`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  place-items: center;
  width: 100px;
  height: 100px;
  padding: 10px;
  border: 1px solid black;
`;

const btnInactive = css`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  place-items: center;
  width: 100px;
  height: 100px;
  padding: 10px;
  background-color: #dedede;
  filter: grayscale(1) opacity(0.6);
`;

export default QRplatformPage;
